import axios from '@/plugins/axios';

const initialState = () => {
    return {
        users: [],
        admins: [],
        deletedUsers: [],
        userCount: 0,
        adminCount: 0,
        deletedCount: 0
    };
};

const state = () => initialState();

const getters = {
    getUsers: state => state.users,
    getAdmins: state => state.admins,
    getDeletedUsers: state => state.deletedUsers,
    getUserCount: state => state.userCount,
    getAdminCount: state => state.adminCount,
    getDeletedCount: state => state.deletedCount,
    getUserById: state => (userId, type = 'users') =>
        state[type].find(user => user.id === userId)
};

const mutations = {
    setUsers: (state, users) => {
        state.users = users;
    },
    setAdmins: (state, users) => {
        state.admins = users;
    },
    setDeletedUsers: (state, users) => {
        state.deletedUsers = users;
    },

    setUserCount: (state, count) => {
        state.userCount = count;
    },
    setAdminCount: (state, count) => {
        state.adminCount = count;
    },
    setDeletedCount: (state, count) => {
        state.deletedCount = count;
    },

    addToUserCount: (state, number) => {
        state.userCount += number;
    },
    addToAdminCount: (state, number) => {
        state.adminCount += number;
    },
    addToDeletedCount: (state, number) => {
        state.deletedCount += number;
    },

    addUser: (state, user) => {
        state.users.unshift(user);
    },
    addAdmin: (state, user) => {
        state.admins.unshift(user);
    },
    addDeletedUser: (state, user) => {
        state.deletedUsers.unshift(user);
    },

    deleteUser: (state, user) => {
        state.users = state.users.filter(u => u.id !== user.id);
    },
    deleteAdmin: (state, user) => {
        state.admins = state.admins.filter(u => u.id !== user.id);
    },
    deleteDeletedUser: (state, user) => {
        state.deletedUsers = state.deletedUsers.filter(u => u.id !== user.id);
    },

    setUser: (state, user) => {
        const userIndex = state.users.findIndex(u => u.id === user.id);

        if (~userIndex) {
            state.users.splice(userIndex, 1, user);
        }
    },
    setAdmin: (state, user) => {
        const adminIndex = state.admins.findIndex(u => u.id === user.id);

        if (~adminIndex) {
            state.admins.splice(adminIndex, 1, user);
        }
    },

    clearState: state => {
        state.users = [];
    }
};

const actions = {
    async fetch({ commit }, params) {
        const {
            data: { rows, count }
        } = await axios.get('/users', { params });

        if (params.role === 'admin') {
            commit('setAdmins', rows);
            commit('setAdminCount', count);

            return rows;
        }

        if (params.includeDeleted) {
            commit('setDeletedUsers', rows);
            commit('setDeletedCount', count);

            return rows;
        }

        commit('setUsers', rows);
        commit('setUserCount', count);

        return rows;
    },

    async search(vuexContext, query) {
        const {
            data: { rows }
        } = await axios.get('/users', { params: { query } });

        return rows;
    },

    async save({ commit }, user) {
        if (user.id) {
            const { data } = await axios.put(`/users/${user.id}`, user);

            if (data.roles.some(role => role.name === 'admin')) {
                commit('setAdmin', data);

                return;
            }

            commit('setUser', data);
        } else {
            const { data } = await axios.post('/users', user);

            commit('addUser', data);
            commit('addToUserCount', 1);
        }
    },

    async delete({ commit, getters }, { userId, roles }) {
        await axios.delete(`/users/${userId}`);

        const isAdmin = roles.some(role => role.name === 'admin');

        const user = getters.getUserById(userId, isAdmin ? 'admins' : 'users');

        commit(isAdmin ? 'deleteAdmin' : 'deleteUser', user);
        commit(isAdmin ? 'addToAdminCount' : 'addToUserCount', -1);
        commit('addDeletedUser', user);
        commit('addToDeletedCount', 1);
    },

    async restore({ commit, getters }, { userId, roles }) {
        await axios.put(`/users/${userId}/restore`);

        const isAdmin = roles.some(role => role.name === 'admin');

        const user = getters.getUserById(userId, 'deletedUsers');

        commit('deleteDeletedUser', user);
        commit('addToDeletedCount', -1);
        commit(isAdmin ? 'addAdmin' : 'addUser', user);
        commit(isAdmin ? 'addToAdminCount' : 'addToUserCount', 1);
    },

    async fetchHistoriesByEntityId(vuexContext, entityId) {
        const { data } = await axios.get(`/history/users/${entityId}`);

        return data;
    },

    async importCsv({ dispatch }, csvFile) {
        const csvData = new FormData();

        csvData.append('csvFile', csvFile);

        await axios.post('/users/import-csv', csvData);

        await dispatch('fetch');
    }
};

export default {
    state,
    getters,
    mutations,
    actions,
    namespaced: true
};
